import PropTypes from "prop-types";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import StatusPill from "common/Components/StatusPill";
import { NODE_CONTEXT_IMPORT } from "./NodeContextImportConstants";
import { LOOKUP_REQUIRED_MESSAGE, formatCodeForDisplay } from "./ContextImportValidationCodes";
import { downloadFailureReportCsv } from "./ContextImportCsvReport";
import { VALIDATION_VERDICT, formatCount } from "./ContextImportValidationResult";
import {
  ImportRunItem,
  RunHeaderRow,
  ImportRunFile,
  RunSummaryMetrics,
  RunToggleButton,
  ExpandedRunPanel,
  ResultsText,
  MetricsGrid,
  MetricBlock,
  MetricValue,
  MetricLabel,
  CodesSectionContainer,
  CodesSectionHeader,
  CodesSectionTitle,
  CodesGrid,
  CodeEntry,
  CodeEntrySummary,
  CodeEntryCountPart,
  CodeEntryDescPart,
  CodeLookupNote,
  DownloadReportFooter,
  DownloadReportButton,
} from "./NodeContextImports.styles";

export default function ValidationRunDetails({
  run,
  expanded,
  onToggle,
}) {
  const nci = NODE_CONTEXT_IMPORT;
  const vr = run.validationResult;
  const panelId = `import-run-panel-${run.id}`;
  const isFailed =
    vr?.verdict === VALIDATION_VERDICT.FAILED ||
    vr?.verdict === VALIDATION_VERDICT.REFUSED;
  const isRefused = vr?.verdict === VALIDATION_VERDICT.REFUSED;
  const showCodesSection =
    nci.validationResults.showCodesInFileSection && vr.codeLegend.length > 0;

  return (
    <ImportRunItem>
      <RunHeaderRow>
        <StatusPill label={run.chip} tone={run.tone} />
        <ImportRunFile component="span" title={run.file}>
          {run.file}
        </ImportRunFile>
        <RunSummaryMetrics>{run.summaryLine}</RunSummaryMetrics>
        <RunToggleButton
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggle}
        >
          {nci.validationResults.details}
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16 }} aria-hidden />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16 }} aria-hidden />
          )}
        </RunToggleButton>
      </RunHeaderRow>

      {expanded && vr ? (
        <ExpandedRunPanel id={panelId} role="region" aria-label={run.file}>
          {isFailed ? (
            <>
              {isRefused ? <ResultsText>{vr.resultsText}</ResultsText> : null}
              <MetricsGrid>
                <MetricBlock>
                  <MetricValue>{formatCount(vr.totalNodes)}</MetricValue>
                  <MetricLabel>{nci.validationResults.metrics.nodes}</MetricLabel>
                </MetricBlock>
                <MetricBlock>
                  <MetricValue>{formatCount(vr.createdNodes)}</MetricValue>
                  <MetricLabel>{nci.validationResults.metrics.toCreate}</MetricLabel>
                </MetricBlock>
                <MetricBlock>
                  <MetricValue>{formatCount(vr.updatedNodes)}</MetricValue>
                  <MetricLabel>{nci.validationResults.metrics.toUpdate}</MetricLabel>
                </MetricBlock>
                <MetricBlock>
                  <MetricValue>{formatCount(vr.failedNodes)}</MetricValue>
                  <MetricLabel>{nci.validationResults.metrics.failed}</MetricLabel>
                </MetricBlock>
              </MetricsGrid>

              {showCodesSection ? (
                <CodesSectionContainer>
                  <CodesSectionHeader>
                    <CodesSectionTitle>
                      {nci.validationResults.codesTitle}
                    </CodesSectionTitle>
                  </CodesSectionHeader>
                  <CodesGrid>
                    {vr.codeLegend.map((entry) => (
                      <CodeEntry key={entry.code}>
                        <StatusPill
                          label={formatCodeForDisplay(entry.code)}
                          tone="crit"
                        />
                        <CodeEntrySummary component="p">
                          <CodeEntryCountPart>
                            {formatCount(entry.count)} node
                            {entry.count === 1 ? "" : "s"}
                          </CodeEntryCountPart>
                          {" · "}
                          <CodeEntryDescPart>{entry.description}</CodeEntryDescPart>
                        </CodeEntrySummary>
                        {entry.lookupRequired ? (
                          <CodeLookupNote>
                            <SearchIcon className="lookup-icon" sx={{ fontSize: 14 }} aria-hidden />
                            <span>{LOOKUP_REQUIRED_MESSAGE}</span>
                          </CodeLookupNote>
                        ) : null}
                      </CodeEntry>
                    ))}
                  </CodesGrid>
                </CodesSectionContainer>
              ) : null}

              <DownloadReportFooter>
                <DownloadReportButton
                  type="button"
                  onClick={() => downloadFailureReportCsv(vr)}
                  aria-label={nci.buttons.downloadReport}
                >
                  <DownloadIcon sx={{ fontSize: 15 }} aria-hidden />
                  {nci.buttons.downloadReport}
                </DownloadReportButton>
              </DownloadReportFooter>
            </>
          ) : (
            <ResultsText>{vr.resultsText}</ResultsText>
          )}
        </ExpandedRunPanel>
      ) : null}
    </ImportRunItem>
  );
}

ValidationRunDetails.propTypes = {
  run: PropTypes.shape({
    id: PropTypes.string.isRequired,
    file: PropTypes.string.isRequired,
    chip: PropTypes.string.isRequired,
    tone: PropTypes.string.isRequired,
    summaryLine: PropTypes.string,
    validationResult: PropTypes.object,
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
